import { useState, useRef } from 'react';
import { router, Link } from '@inertiajs/react';
import axios from 'axios';
import GmLayout from '@/layouts/GmLayout';

/**
 * Book data from the backend.
 */
interface TomeBook {
    id: number;
    title: string;
    slug: string;
}

/**
 * Chapter data from the backend.
 */
interface TomeChapter {
    id: number;
    title: string;
    description: string | null;
    order: number;
}

/**
 * Page data from the backend.
 */
interface TomePage {
    id: number;
    name: string;
    slug: string;
    order: number;
    is_published: boolean;
}

/**
 * Props for the Edit page.
 */
interface Props {
    book: TomeBook;
    chapter: TomeChapter;
    pages: TomePage[];
}

/**
 * GM Tome Chapter Edit page - form for editing a chapter and viewing its pages.
 */
export default function Edit({ book, chapter, pages }: Props) {
    const [title, setTitle] = useState(chapter.title);
    const [description, setDescription] = useState(chapter.description || '');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Sorting state
    const [isSorting, setIsSorting] = useState(false);
    const [sortedPages, setSortedPages] = useState<TomePage[]>(pages);
    const [isSavingOrder, setIsSavingOrder] = useState(false);

    // Drag state for visual feedback
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const dragNode = useRef<HTMLDivElement | null>(null);

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        router.patch(`/gm/tome/books/${book.id}/chapters/${chapter.id}`, {
            title,
            description: description || null,
        }, {
            onError: (errors) => {
                setErrors(errors);
                setIsSubmitting(false);
            },
            onSuccess: () => {
                setIsSubmitting(false);
            },
        });
    };

    // Enter sort mode
    const startSorting = () => {
        setSortedPages([...pages]);
        setIsSorting(true);
    };

    // Cancel sorting and reset to original order
    const cancelSorting = () => {
        setSortedPages([...pages]);
        setIsSorting(false);
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    // Save the new order to the backend using axios (since endpoint returns JSON, not Inertia response)
    const saveOrder = async () => {
        setIsSavingOrder(true);

        // Send array of page IDs in the new order
        const pageIds = sortedPages.map(p => p.id);

        try {
            await axios.post('/gm/tome/reorder', { pages: pageIds });

            // Reload the page to get fresh data from the server
            router.reload({ only: ['pages'], preserveScroll: true });

            setIsSorting(false);
            setIsSavingOrder(false);
        } catch {
            setIsSavingOrder(false);
            alert('Failed to save page order. Please try again.');
        }
    };

    // Handle drag start
    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        setDraggedIndex(index);
        dragNode.current = e.currentTarget;

        // Set drag image and data
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());

        // Add a slight delay to allow the drag image to be set before adding styles
        setTimeout(() => {
            if (dragNode.current) {
                dragNode.current.style.opacity = '0.5';
            }
        }, 0);
    };

    // Handle drag over another item
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        if (draggedIndex === null || draggedIndex === index) return;

        setDragOverIndex(index);
    };

    // Handle drag end (cleanup)
    const handleDragEnd = () => {
        if (dragNode.current) {
            dragNode.current.style.opacity = '1';
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
        dragNode.current = null;
    };

    // Handle drop
    const handleDrop = (e: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
        e.preventDefault();

        if (draggedIndex === null || draggedIndex === dropIndex) {
            handleDragEnd();
            return;
        }

        // Reorder the pages array
        const newPages = [...sortedPages];
        const [draggedPage] = newPages.splice(draggedIndex, 1);
        newPages.splice(dropIndex, 0, draggedPage);

        setSortedPages(newPages);
        handleDragEnd();
    };

    // Get the pages to display (sorted or original)
    const displayPages = isSorting ? sortedPages : pages;

    return (
        <GmLayout title={`Edit: ${chapter.title}`} activePath="/gm/tome/books">
            <div className="max-w-4xl">
                <div className="flex items-center gap-4 mb-6">
                    <Link href={`/gm/tome/books/${book.id}`} className="text-gray-400 hover:text-white transition-colors">
                        ← Back to {book.title}
                    </Link>
                </div>

                <h1 className="text-2xl font-bold text-white mb-6">📑 Edit Chapter</h1>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Chapter Details Form */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                        <h2 className="text-lg font-semibold text-white mb-4">Chapter Details</h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    required
                                />
                                {errors.title && <p className="mt-1 text-sm text-red-400">{errors.title}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={3}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                />
                            </div>
                            <button type="submit" disabled={isSubmitting} className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 text-white rounded transition-colors">
                                {isSubmitting ? 'Saving...' : 'Save Changes'}
                            </button>
                        </form>
                    </div>

                    {/* Pages Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-white">Pages</h2>
                            <div className="flex items-center gap-2">
                                {/* Sort mode controls */}
                                {isSorting ? (
                                    <>
                                        <button
                                            onClick={saveOrder}
                                            disabled={isSavingOrder}
                                            className="px-3 py-1 text-sm bg-green-600 hover:bg-green-700 disabled:bg-green-800 text-white rounded transition-colors"
                                        >
                                            {isSavingOrder ? 'Saving...' : 'Save Order'}
                                        </button>
                                        <button
                                            onClick={cancelSorting}
                                            disabled={isSavingOrder}
                                            className="px-3 py-1 text-sm bg-slate-600 hover:bg-slate-500 disabled:bg-slate-700 text-white rounded transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        {/* Only show Sort button if there are 2+ pages */}
                                        {pages.length >= 2 && (
                                            <button
                                                onClick={startSorting}
                                                className="px-3 py-1 text-sm bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                                            >
                                                Sort Pages
                                            </button>
                                        )}
                                        <Link
                                            href={`/gm/tome/create?chapter_id=${chapter.id}`}
                                            className="px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                                        >
                                            + Add Page
                                        </Link>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Sort mode instructions */}
                        {isSorting && (
                            <div className="mb-4 p-3 bg-blue-900/30 border border-blue-700/50 rounded text-sm text-blue-200">
                                Drag pages to reorder them. Click "Save Order" when done.
                            </div>
                        )}

                        {pages.length === 0 ? (
                            <p className="text-gray-400 text-sm">No pages yet. Add a page to start writing content.</p>
                        ) : (
                            <div className="space-y-2">
                                {displayPages.map((page, index) => (
                                    <div
                                        key={page.id}
                                        draggable={isSorting}
                                        onDragStart={(e) => isSorting && handleDragStart(e, index)}
                                        onDragOver={(e) => isSorting && handleDragOver(e, index)}
                                        onDragEnd={handleDragEnd}
                                        onDrop={(e) => isSorting && handleDrop(e, index)}
                                        className={`flex items-center justify-between p-3 bg-slate-700 rounded transition-all ${
                                            isSorting ? 'cursor-grab active:cursor-grabbing' : ''
                                        } ${
                                            draggedIndex === index ? 'opacity-50' : ''
                                        } ${
                                            dragOverIndex === index && draggedIndex !== index
                                                ? 'border-2 border-purple-500 border-dashed'
                                                : 'border-2 border-transparent'
                                        }`}
                                    >
                                        {/* Drag handle - only visible in sort mode */}
                                        {isSorting && (
                                            <div className="mr-3 text-gray-400 select-none">
                                                <span className="text-lg">⋮⋮</span>
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white font-medium truncate">{page.name}</p>
                                            <p className="text-xs text-gray-400 truncate">/{page.slug}</p>
                                        </div>
                                        <div className="flex items-center gap-2 ml-2">
                                            <span className={`px-2 py-0.5 text-xs rounded ${page.is_published ? 'bg-green-600' : 'bg-yellow-600'}`}>
                                                {page.is_published ? 'Published' : 'Draft'}
                                            </span>
                                            {/* Hide Edit button in sort mode to prevent accidental navigation */}
                                            {!isSorting && (
                                                <Link href={`/gm/tome/${page.id}/edit`} className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded">Edit</Link>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </GmLayout>
    );
}

